import { type ReactNode, type Ref, useRef, useState } from "react";

import { CalendarDateTime, createCalendar, getLocalTimeZone } from "@internationalized/date";
import {
  type AriaDateRangePickerProps,
  type DateValue,
  useDateFormatter,
  useDateRangePicker,
  useLocale,
} from "react-aria";
import { Dialog } from "react-aria-components";
import { useDateFieldState, useDateRangePickerState } from "react-stately";

import { SelectField } from "#components/SelectField";
import { CalendarIcon } from "#icons";
import {
  useTimezoneOptions,
  initializeYearOptions,
  isDateTimeType,
  rangeToZonedRange,
  type RangeValue,
  type MappedDateValue,
} from "#utils";

import { RangeCalendar } from "../DateRangePicker/RangeCalendar";
import { Popover } from "../DatePicker/Popover";

/** Props passed to a custom `renderTrigger`, keeping the picker's display and open behavior */
export interface DateRangeSelectTriggerProps {
  /** Formatted range or preset label; undefined when nothing is selected */
  value?: string;
  /** Placeholder to show when `value` is undefined */
  placeholder: string;
  /** Whether the popover is open */
  isOpen: boolean;
  /** Whether the field is disabled */
  isDisabled: boolean;
  /** Whether the field is invalid */
  isInvalid: boolean;
  /** Ref to attach to the focusable trigger element — the popover anchors to it */
  ref: Ref<HTMLButtonElement>;
  /** Toggle the popover open/closed */
  onPress: () => void;
}

/** DateRangeSelectProps interface for the DateRangeSelect component */
export interface DateRangeSelectProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
  /** Called with the selected range as ISO strings whenever it changes */
  onRangeChange: (range: { start: string; end: string }) => void;
  /** Date granularity — "day" shows a calendar, "second" adds time selection */
  granularity?: "second" | "day";
  /** Selectable years for the month/year navigation */
  yearOptions?: number[];
  /** Assistive text displayed below the trigger */
  assistiveText?: ReactNode;
  /** Tooltip text shown next to the label */
  tooltipText?: ReactNode;
  /** Class name applied to the outer wrapper */
  className?: string;
  /** Class name applied to the trigger button (e.g. for rounded corners) */
  buttonClassName?: string;
  /** Placeholder shown in the trigger when no range is selected */
  placeholder?: string;
  /** Leading content inside the trigger; defaults to a calendar icon. Pass `null` to omit */
  startAdornment?: ReactNode;
  /** Render a custom trigger instead of the default SelectField; attach `ref` to a focusable element and call `onPress` to toggle the popover */
  renderTrigger?: (props: DateRangeSelectTriggerProps) => ReactNode;
}

/**
 * A DateRangePicker with a SelectField-style trigger. Opens the same popover,
 * so users can pick a custom range on the calendar or a built-in preset.
 */
export function DateRangeSelect<T extends DateValue>({
  yearOptions,
  granularity = "day",
  onRangeChange,
  assistiveText,
  tooltipText,
  className,
  buttonClassName,
  placeholder = "Select date range",
  startAdornment = <CalendarIcon className="size-lg" />,
  renderTrigger,
  ...props
}: DateRangeSelectProps<T>) {
  const { years, minDate, maxDate } = initializeYearOptions(
    yearOptions,
    props.minValue,
    props.maxValue,
  );

  const { locale } = useLocale();

  const ref = useRef<HTMLButtonElement>(null);

  const { userTimezone, timezoneOptions } = useTimezoneOptions();
  const [timezone, setTimezone] = useState(userTimezone);

  // Show the preset label (e.g. "This Month") instead of the expanded range;
  // cleared when the user picks custom dates on the grid.
  const [presetLabel, setPresetLabel] = useState<string | null>(null);

  function onChange(range: RangeValue<MappedDateValue<T>> | null) {
    if (!range) return;

    // Editing the date fields directly clears the preset label.
    setPresetLabel(null);

    if (timezone && isDateTimeType(range.start) && isDateTimeType(range.end)) {
      try {
        onRangeChange(rangeToZonedRange(range.start, range.end, timezone, locale));
      } catch (e) {
        console.error(e);
      }
    } else {
      onRangeChange({
        start: range.start.toDate("UTC").toString(),
        end: range.end.toDate("UTC").toString(),
      });
    }
  }

  const state = useDateRangePickerState({
    ...props,
    shouldCloseOnSelect: false,
    granularity,
    shouldForceLeadingZeros: true,
    maxValue: maxDate,
    minValue: minDate,
    onChange,
  });

  const { startFieldProps, endFieldProps, dialogProps, calendarProps } = useDateRangePicker(
    {
      ...props,
      granularity,
      shouldForceLeadingZeros: true,
      maxValue: maxDate,
      minValue: minDate,
      onChange,
    },
    state,
    ref,
  );

  const startFieldState = useDateFieldState({
    ...startFieldProps,
    locale,
    createCalendar,
  });
  const endFieldState = useDateFieldState({
    ...endFieldProps,
    locale,
    createCalendar,
  });

  const formatter = useDateFormatter(
    granularity === "second"
      ? { dateStyle: "medium", timeStyle: "short" }
      : { dateStyle: "medium" },
  );

  const formattedRange =
    state.value?.start && state.value?.end
      ? `${formatter.format(state.value.start.toDate(getLocalTimeZone()))} – ${formatter.format(
          state.value.end.toDate(getLocalTimeZone()),
        )}`
      : undefined;

  const displayValue = presetLabel ?? formattedRange;

  const isInvalid = props.isInvalid || state.realtimeValidation.isInvalid;
  const isDisabled = props.isDisabled ?? false;

  const resolvedAssistiveText = state.realtimeValidation.validationErrors.length
    ? state.realtimeValidation.validationErrors.join(", ")
    : assistiveText;

  // SelectField's label/aria-label are strings; AriaDateRangePickerProps.label is a ReactNode.
  const labelText = typeof props.label === "string" ? props.label : undefined;

  const togglePopover = () => state.setOpen(!state.isOpen);

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          value: displayValue,
          placeholder,
          isOpen: state.isOpen,
          isDisabled,
          isInvalid,
          ref,
          onPress: togglePopover,
        })
      ) : (
        <SelectField
          aria-label={props["aria-label"] ?? labelText ?? "Select date range"}
          assistiveText={resolvedAssistiveText}
          buttonClassName={buttonClassName}
          className={className}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isOpen={state.isOpen}
          isReadOnly={props.isReadOnly}
          isRequired={props.isRequired}
          label={labelText}
          onPress={togglePopover}
          placeholder={placeholder}
          ref={ref}
          startAdornment={startAdornment}
          tooltipText={tooltipText}
          value={displayValue}
        />
      )}

      {state.isOpen ? (
        <Popover placement="bottom start" state={state} triggerRef={ref}>
          <Dialog
            {...dialogProps}
            className="w-[22.5rem] rounded-md border border-outline-decorative bg-background-primary p-xl shadow-md"
          >
            <RangeCalendar
              {...calendarProps}
              dateFieldEndState={endFieldState}
              dateFieldStartState={startFieldState}
              dateRangePickerState={state}
              endFieldProps={endFieldProps}
              firstDayOfWeek={props.firstDayOfWeek}
              onChange={(dateRange) => {
                // A custom grid selection supersedes any preset label.
                setPresetLabel(null);
                state.setDateRange(dateRange);

                // Clicking a date range in the calendar sets the start time to
                // 00:00:00 and the end time to 23:59:59, matching DateRangePicker.
                if (granularity === "second") {
                  const newRange = {
                    start: new CalendarDateTime(
                      dateRange.start.year,
                      dateRange.start.month,
                      dateRange.start.day,
                      0,
                      0,
                      0,
                    ),
                    end: new CalendarDateTime(
                      dateRange.end.year,
                      dateRange.end.month,
                      dateRange.end.day,
                      23,
                      59,
                      59,
                    ),
                  };
                  state.setValue(newRange);
                }
              }}
              onPresetSelect={setPresetLabel}
              setTimezone={setTimezone}
              startFieldProps={startFieldProps}
              timezone={timezone}
              timezoneOptions={timezoneOptions}
              yearOptions={years}
            />
          </Dialog>
        </Popover>
      ) : null}
    </>
  );
}
